import React, {createContext, useContext, useEffect, useState} from 'react';
import {httpsCallable} from 'firebase/functions';
import {functions} from './firebase';
import {ThemeProvider} from 'styled-components';
import {useTranslation} from 'react-i18next';
import setDynamicFavicon from './utils/setDynamicFavicon';

const Context = createContext();

export function useRentUserAndWebsiteConfigurationContext() {
    return useContext(Context);
}

const getInitials = (name) => {
    const words = name.split(' ');
    if (words.length >= 2) {
        return words
            .slice(0, 2)
            .map(word => word.charAt(0).toUpperCase())
            .join('');
    }
    return name.slice(0, 2).toUpperCase();
};

export function RentalProvider({children}) {
    const [rentUser, setRentUser] = useState(null);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const {i18n} = useTranslation();

    const theme = {
        colors: {
            primary: config?.primaryColor || '#007bff',
            secondary: config?.secondaryColor || '#6c757d',
            text: config?.text || '#333333',
            background: config?.background || '#f8f9fa',
            backgroundItemCard: config?.backgroundItemCard || '#ffffff',
        },
    };

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const hostname = window.location.hostname;
                const searchParams = new URLSearchParams(window.location.search);
                const queryRent = searchParams.get('rent');
                if (queryRent) {
                    sessionStorage.setItem('locem_dev_rent', queryRent);
                }
                const storedRent = sessionStorage.getItem('locem_dev_rent');
                const subdomainMatch = hostname.match(/^(.+)\.(locemsolutions\.com|localhost)$/);
                const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
                const rentName = queryRent || (subdomainMatch ? subdomainMatch[1] : null) || (isLocal ? storedRent : null);

                if (!rentName) {
                    throw new Error('Invalid subdomain');
                }

                const currentLang = (i18n.resolvedLanguage || i18n.language || 'it').split('-')[0].toLowerCase();
                const normalizedLang = ['it', 'en', 'de'].includes(currentLang) ? currentLang : 'it';

                const rentUserAndWebsiteConfigurationView = httpsCallable(functions, 'rentUserAndWebsiteConfigurationView');
                const response = await rentUserAndWebsiteConfigurationView({rentName, lang: normalizedLang});

                const rentUser = response.data.rentUser;
                const websiteConfiguration = response.data.websiteConfiguration;

                if (!rentUser || !websiteConfiguration) {
                    throw new Error('Configuration data missing');
                }

                setRentUser(rentUser);
                setConfig(websiteConfiguration);
                setLoading(false);

                const initials = getInitials(rentUser.name);
                setDynamicFavicon(
                    initials,
                    websiteConfiguration.primaryColor,
                    websiteConfiguration.background
                );

                document.title = rentUser.name;
            } catch (err) {
                console.error('Error fetching configuration:', err);
                setError(err.message === 'Invalid subdomain' ? 'Invalid subdomain' : 'Failed to load configuration');
                setLoading(false);
            }
        };
        fetchConfig();
    }, [i18n.language, i18n.resolvedLanguage]);

    return (
        <Context.Provider value={{rentUser, config, loading, error}}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </Context.Provider>
    );
}
